import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PageLayout from '../layout/PageLayout';
import DataTable from '../ui/DataTable';
import HtmlPreview from '../ui/HtmlPreview';
import StatusChip from '../ui/StatusChip';
import ActionIconButton from '../ui/ActionIconButton';
import { notify } from '../feedback/notify';
import { FormatDate } from '../../utils/FormatDate';

/**
 * Página genérica para documentos legais versionados
 * (termos de uso, política de privacidade, contratos).
 */
export default function DocumentosVersionadosPage({
    title,
    subtitle,
    listEndpoint,
    responseKey,
    getId,
    addLabel,
    emptyMessage,
    renderAddDialog,
}) {
    const token = localStorage.getItem('token');

    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adicionar, setAdicionar] = useState(false);
    const [visualizar, setVisualizar] = useState(null);

    useEffect(() => {
        const getDocumentos = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/${listEndpoint}`, {
                    headers: {
                        Authorization: token
                    }
                })
                setDocumentos(response.data[responseKey] || []);
            } catch (error) {
                console.error(`Erro ao listar ${title}:`, error);
                notify.error('Não foi possível carregar os documentos.');
            } finally {
                setLoading(false);
            }
        }

        getDocumentos();
    }, [token, adicionar, listEndpoint, responseKey, title])

    // A versão mais recente (maior data de criação) é a vigente.
    const idVigente = documentos.reduce((maisRecente, doc) => {
        if (!maisRecente) return doc;
        return new Date(doc?.DataCriacao) > new Date(maisRecente?.DataCriacao) ? doc : maisRecente;
    }, null);

    const columns = [
        { key: 'id', label: 'ID', width: 72, render: (doc) => getId(doc) },
        {
            key: 'versao',
            label: 'Versão',
            width: 160,
            render: (doc) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>{doc?.Versao}</Box>
                    {idVigente && getId(doc) === getId(idVigente) && <StatusChip label="Vigente" tone="success" />}
                </Box>
            ),
        },
        { key: 'conteudo', label: 'Conteúdo', minWidth: 320, wrap: true, render: (doc) => <HtmlPreview html={doc?.Conteudo} /> },
        { key: 'criadoEm', label: 'Criado em', width: 120, render: (doc) => (doc?.DataCriacao ? FormatDate(doc.DataCriacao) : '—') },
        {
            key: 'acoes',
            label: 'Ações',
            align: 'right',
            width: 80,
            render: (doc) => <ActionIconButton title="Visualizar" icon={<VisibilityOutlinedIcon fontSize="small" />} onClick={() => setVisualizar(doc)} />,
        },
    ];

    return (
        <PageLayout
            title={title}
            subtitle={subtitle}
            breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: title }]}
            actions={
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdicionar(true)}>
                    {addLabel}
                </Button>
            }
        >
            <DataTable
                columns={columns}
                rows={documentos}
                getRowKey={(doc, i) => getId(doc) ?? i}
                loading={loading}
                emptyMessage={emptyMessage}
            />

            {renderAddDialog({ open: adicionar, onClose: () => setAdicionar(false) })}

            <Dialog open={Boolean(visualizar)} onClose={() => setVisualizar(null)} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle sx={{ pr: 7, borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
                    {title} — versão {visualizar?.Versao}
                    <IconButton aria-label="Fechar" onClick={() => setVisualizar(null)} size="small" sx={{ position: 'absolute', right: 14, top: 16, color: 'text.secondary' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: '20px !important', fontSize: '0.875rem', lineHeight: 1.7, '& p': { mb: 1 }, '& ul, & ol': { pl: 3, mb: 1 } }}>
                    <div dangerouslySetInnerHTML={{ __html: visualizar?.Conteudo || '' }} />
                </DialogContent>
            </Dialog>
        </PageLayout>
    );
}
